import { ADSBPositions } from './models';

export type PlaneHistory = { [modeS: string]: ADSBPositions[] };

// Positions kept per plane (the trail drawn for the selected plane).
export const PLANE_HISTORY_LENGTH = 15;
// A plane that has not been reported for this long is dropped from the history.
export const PLANE_STALE_MS = 10 * 60 * 1000;

/**
 * Appends the latest positions to each plane's trail (capped to PLANE_HISTORY_LENGTH) and drops
 * the planes that have not been seen for PLANE_STALE_MS, so the history cannot grow with every
 * aircraft ever reported. `lastSeen` maps modeS -> timestamp of its latest report.
 */
export function updatePlaneHistory(
  history: PlaneHistory,
  positions: ADSBPositions[],
  lastSeen: { [modeS: string]: number },
  now: number,
): PlaneHistory {
  const updated: PlaneHistory = {};
  Object.keys(history).forEach((modeS) => {
    if (now - (lastSeen[modeS] ?? 0) <= PLANE_STALE_MS) {
      updated[modeS] = history[modeS];
    }
  });
  positions.forEach((position) => {
    const trail = updated[position.modeS] || [];
    updated[position.modeS] = [...trail.slice(-(PLANE_HISTORY_LENGTH - 1)), position];
  });
  return updated;
}

/**
 * Trail of the selected plane up to its current position, without mutating the stored history
 * (the current position is added only if the history does not already end with it).
 */
export function buildPlaneTrail(
  history: ADSBPositions[],
  current: ADSBPositions | undefined,
): ADSBPositions[] {
  if (!current) return history;
  const last = history[history.length - 1];
  if (last && last.time === current.time) return history;
  return [...history, current].slice(-PLANE_HISTORY_LENGTH);
}
